<template>
  <BittsModal
    v-if="visible"
    :disabled="isFolderNameInvalid || !folderName.trim()"
    secondary-button-text="Cancel"
    title="Create Folder"
    primary-button-text="Create"
    :width="360"
    :use-mask-to-close="true"
    @closed="onClose"
    @action="onSave"
  >
    <div class="flex flex-col">
      <BittsInput
        v-model="folderName"
        :status="folderNameStatus"
        :danger-text="folderNameDangerText"
        :focus="focus"
        name="folder-name-input"
        placeholder="Folder Name"
        @blur="hasInteractedWithName = true"
      />
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsInput, BittsModal } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import { useFlashesStore, useReportsStore } from '@/stores';

interface Props {
  visible: boolean;
  reportIds?: string[];
  focus?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  reportIds: () => [],
  focus: false,
});

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'closed' | 'created'): void;
}>();

const reportsStore = useReportsStore();
const { folders } = storeToRefs(reportsStore);
const flashesStore = useFlashesStore();
const folderName = ref('');

const isFolderNameInvalid = computed(() => {
  const trimmedName = folderName.value.trim();
  if (!trimmedName) return false;
  return !!folders.value.find((folder) => folder.name === trimmedName);
});

const hasInteractedWithName = ref(false);
const isNameRequired = computed(
  () => hasInteractedWithName.value && !folderName.value.trim(),
);

const folderNameStatus = computed<'danger' | 'default'>(() =>
  isFolderNameInvalid.value || isNameRequired.value ? 'danger' : 'default',
);

const folderNameDangerText = computed(() =>
  isFolderNameInvalid.value
    ? 'Folder must have unique name.'
    : 'A name is required.',
);

function onClose() {
  folderName.value = '';
  emit('update:visible', false);
  emit('closed');
}

async function onSave() {
  const payload = {
    name: folderName.value.trim(),
    reports: props.reportIds,
  };

  try {
    await crossbeamApi.POST('/v0.1/report-folders', {
      body: payload,
    });
    flashesStore.addSuccessFlash({
      message: 'List folder created',
    });
    folderName.value = '';
    await reportsStore.refreshReportsStore();
    emit('update:visible', false);
    emit('created');
  } catch (_err) {
    flashesStore.addErrorFlash({
      message: 'Something went wrong',
      description: 'This folder could not be saved',
    });
  }
}

watch(
  () => props.visible,
  (newValue) => {
    if (!newValue) {
      folderName.value = '';
      hasInteractedWithName.value = false;
    }
  },
);
</script>
